import { IntegrityMonitorRulesAppView } from '../view/IntegrityMonitorRulesAppView';
import { templateIntegrityMonitorRulesApp } from '../view/template';
import { BaseWebComponentController, WebComponentEvent } from '../../../../framework/BaseWebComponentController';
import { IntegrityMonitorRulesAppWebComponent } from '../index';
import { SmartViewEvents } from '../../../../grid/gridSmartViews/types';
import {
  GridActions,
  IntegrityMonitorConfigurationStatus,
  IntegrityMonitoringGridEvents,
  IntegrityMonitorRuleGridItem,
} from '../../integrityMonitorRulesGrid/types';
import { RuleSetFormEvents } from '../../integrityMonitorRuleSetForm/types';
import { DefaultSmartViewId, IntegrityMonitorInlineActions } from '../../integrityMonitorRulesSmartViews/types';
import { CustomRuleFormEvents } from '../../integrityMonitorCustomRules/imAddCustomRulePanel/types';
import { CommonEvents } from '@nirvana/shared/constants';
import { DefaultRuleConfigurationEvents } from '../../integrityMonitorDefaultRuleConfiguration/types';
import { AssignRulesFormEvents, RuleSetOption } from '../../integrityMonitorAssignRulesForm/types';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { IntegrityMonitorRulesAppRepository } from '../repository/IntegrityMonitorDefaultRuleConfigurationRepository';
import { IntegrityMonitorRulesAppWebComponentSchema } from '../types';
import { showErrorToast, showSuccessToast } from '@nirvana/shared/common';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';

export class IntegrityMonitorRulesAppController extends BaseWebComponentController {
  public component!: IntegrityMonitorRulesAppWebComponent;
  public view!: IntegrityMonitorRulesAppView;
  public viewConstructor!: typeof IntegrityMonitorRulesAppView;
  public dataRepository!: IntegrityMonitorRulesAppRepository;
  public dataRepositoryConstructor!: typeof IntegrityMonitorRulesAppRepository;

  constructor(component: IntegrityMonitorRulesAppWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: IntegrityMonitorRulesAppView,
      viewTemplate: templateIntegrityMonitorRulesApp,
      dataRepositoryConstructor: IntegrityMonitorRulesAppRepository,
    });
  }

  async getDataForView(): Promise<IntegrityMonitorRulesAppWebComponentSchema> {
    if (this.component.ruleIdsFilter.length > 0) {
      // Whenever ruleIdsFilter is active, "All Rules" view should be selected
      this.component.currentViewId = DefaultSmartViewId.ALL_RULES;
    }

    return {
      activeViewId: this.component.currentViewId,
      ruleIdsFilter: this.component.ruleIdsFilter,
      availableCompanies: await this.dataRepository.getAvailableCompanies(),
    };
  }

  computeEventListeners() {
    this.view.dom.$companySelector.onCompanyClicked = this.companyChangedHandler.bind(this);

    return [
      // Events from Smart Views
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.handleSmartViewClicked,
      },
      {
        element: this.view.dom.$smartViews,
        event: IntegrityMonitorInlineActions.EDIT,
        callback: this.showEditRuleSetForm,
      },
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_DELETED,
        callback: this.handleSmartViewDeleted,
      },

      // Events from Grid
      {
        element: this.view.dom.$integrityMonitorGrid,
        event: IntegrityMonitoringGridEvents.CLICK_ON_DEFAULT_RULE_NAME,
        callback: this.onClickOnRuleName,
      },
      {
        element: this.view.dom.$integrityMonitorGrid,
        event: IntegrityMonitoringGridEvents.CLICK_ON_CUSTOM_RULE_NAME,
        callback: this.showEditCustomRuleForm,
      },
      {
        element: this.view.dom.$integrityMonitorGrid,
        event: GridActions.NEW_RULE,
        callback: this.showNewCustomRuleForm,
      },
      {
        element: this.view.dom.$integrityMonitorGrid,
        event: GridActions.NEW_RULE_SET,
        callback: this.showNewRuleSetForm,
      },
      {
        element: this.view.dom.$integrityMonitorGrid,
        event: GridActions.ASSIGN_RULES_TO_RULE_SETS,
        callback: this.showAssignRulesForm,
      },
      {
        element: this.view.dom.$integrityMonitorGrid,
        event: GridActions.DELETE_RULES,
        callback: this.onDeleteRules,
      },
      {
        element: this.view.dom.$integrityMonitorGrid,
        event: GridActions.DELETE_RULE,
        callback: this.onDeleteRule,
      },
      {
        element: this.view.dom.$integrityMonitorGrid,
        event: GridActions.CORRECT_RULE,
        callback: this.onCorrectRule,
      },

      // Events from Rule Set Form
      {
        element: this.view.dom.$integrityMonitorRuleSetForm,
        event: RuleSetFormEvents.RULE_SET_CREATED,
        callback: this.onRuleSetCreated,
      },
      {
        element: this.view.dom.$integrityMonitorRuleSetForm,
        event: RuleSetFormEvents.RULE_SET_EDITED,
        callback: this.onRuleSetEdited,
      },

      // Events from Assign Rules Form
      {
        element: this.view.dom.$integrityMonitorAssignRulesForm,
        event: AssignRulesFormEvents.ASSIGNED_RULES,
        callback: this.refreshGrid,
      },

      // Events from Custom Rule Form
      {
        element: this.view.dom.$integrityMonitorCustomRuleForm,
        event: CustomRuleFormEvents.CUSTOM_RULE_SAVED,
        callback: this.onCustomRuleSave,
      },

      // Events from Default Rule Form
      {
        element: this.view.dom.$integrityMonitorDefaultRuleCustomizationForm,
        event: CommonEvents.CLICK_ON_RULE_SET_NAME,
        callback: this.onRuleSetNameClick,
      },
      {
        element: this.view.dom.$integrityMonitorDefaultRuleCustomizationForm,
        event: CommonEvents.CLICK_ON_POLICY_NAME,
        callback: this.onPolicyNameClick,
      },
      {
        element: this.view.dom.$integrityMonitorDefaultRuleCustomizationForm,
        event: DefaultRuleConfigurationEvents.DEFAULT_RULE_SAVED,
        callback: this.refreshGrid,
      },
    ];
  }

  private async changeActiveViewInChildren(newActiveViewId: string) {
    const data = {
      activeViewId: newActiveViewId,
      ruleIdsFilter: [], // Reset the ruleIdsFilter whenever the user is changing the view
    };

    this.component.currentViewId = newActiveViewId;
    await this.view.buildSmartViews(newActiveViewId);
    await this.view.buildIntegrityMonitorGrid(data);
  }

  private async handleSmartViewClicked(event: WebComponentEvent) {
    const smartViewConfiguration = event.detail.smartViewConfiguration;

    if (this.view.dom.$smartViews.activeViewId === smartViewConfiguration.id) {
      // Do nothing. User clicked on an already selected view
      return;
    }

    await this.changeActiveViewInChildren(smartViewConfiguration.id);
  }

  private showEditRuleSetForm(event: WebComponentEvent) {
    const selectedRuleSetId = event.detail.itemData.id;

    this.showRuleSetForm(selectedRuleSetId, undefined);
  }

  private async handleSmartViewDeleted(event: WebComponentEvent) {
    const ruleSetId = event.detail.viewId;
    const companyId = this.dependencies.getActiveCompany().companyId;

    try {
      await this.dataRepository.deleteRuleSet(ruleSetId, companyId);

      if (this.component.currentViewId === ruleSetId) {
        await this.changeActiveViewInChildren(DefaultSmartViewId.ALL_RULES);
      } else {
        await this.view.dom.$smartViews.controller.render();
      }

      showSuccessToast(this.dependencies.translateFn('Rule set deleted successfully.'));
    } catch (error) {
      showErrorToast(this.dependencies.translateFn('Failed to delete rule set.'));
    }
  }

  private showNewRuleSetForm(event: WebComponentEvent) {
    const selectedRules = event.detail.selectedItems;

    this.showRuleSetForm(undefined, selectedRules);
  }

  private showNewCustomRuleForm(event: CustomEvent) {
    this.view.showIntegrityMonitorCustomRuleForm();
    if (event.detail && event.detail.ruleSetId) {
      this.view.dom.$integrityMonitorCustomRuleForm.assignAfterAdd(event.detail.ruleSetId);
    }
  }

  private showEditCustomRuleForm(event: CustomEvent) {
    this.view.showIntegrityMonitorCustomRuleForm();
    this.view.dom.$integrityMonitorCustomRuleForm.setEdit(event.detail.ruleData);
  }

  private showRuleSetForm(ruleSetId: string | undefined, rules: Array<IntegrityMonitorRuleGridItem> | undefined) {
    this.view.dom.$integrityMonitorRuleSetForm.ruleSetId = ruleSetId;
    this.view.dom.$integrityMonitorRuleSetForm.rules = rules;

    this.view.showIntegrityMonitorRuleSetForm();
  }

  private showAssignRulesForm(event: WebComponentEvent) {
    const ruleSetIdentifiers = this.view.dom.$smartViews.ruleSetIdentifiers;
    this.view.dom.$integrityMonitorAssignRulesForm.ruleSetOptions = ruleSetIdentifiers as Array<RuleSetOption>;
    this.view.dom.$integrityMonitorAssignRulesForm.ruleIds = event.detail.selectedItems.map(item => item._id);
    this.view.showIntegrityMonitorAssignRulesForm();
  }

  private async onClickOnRuleName(event: CustomEvent) {
    await this.showDefaultRuleCustomizationForm(event.detail.ruleData);
  }

  private async showDefaultRuleCustomizationForm(ruleData: IntegrityMonitorRuleGridItem) {
    this.view.showIntegrityMonitorDefaultRuleCustomizationForm();

    if (ruleData.configurationStatus === IntegrityMonitorConfigurationStatus.STATUS_SOURCE_UPDATED) {
      // Replace deprecated rule's data with the new version's
      const newRuleData = await this.dataRepository.readRuleOriginal(ruleData.templateId ?? '');
      newRuleData._id = ruleData._id;
      newRuleData.ruleSetIds = ruleData.ruleSetIds;
      newRuleData.createdOn = ruleData.createdOn;
      ruleData = newRuleData;
    }

    await this.view.dom.$integrityMonitorDefaultRuleCustomizationForm.setRuleData(ruleData);
  }

  private async onDeleteRules(event: WebComponentEvent) {
    await this.showDeleteDialog(event.detail.selectedItems);
  }

  private async onDeleteRule(event: WebComponentEvent) {
    await this.showDeleteDialog([event.detail.rowData.item]);
  }

  private async showDeleteDialog(selectedItems: Array<IntegrityMonitorRuleGridItem>) {
    const rulesIds = selectedItems.map(rule => rule._id);
    const assignationDataObject = await this.dataRepository.getRuleDetailedAssignationData(rulesIds);
    const numRules = selectedItems.length;
    let windowTitle, warningText;

    if (selectedItems.length === 1) {
      windowTitle = this.dependencies.translateFn('Delete rule?');

      warningText = this.dependencies.translateFn(
        'Deleting the selected rule will remove it from any rule sets and policies that it is currently assigned to.'
      );
    } else {
      windowTitle = this.dependencies.translateFn('Delete rules?');

      warningText = this.dependencies.translateFn(
        'Deleting the selected rules will remove them from any rule sets and policies that they are currently assigned to.'
      );
    }

    const windowBodyHtml = this.view.getDeleteConfirmationDialogBodyHtml(warningText, numRules, assignationDataObject);

    const windowFooterHtml = this.view.getDeleteConfirmationDialogFooterHtml();

    GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      this.onConfirmDeleteRules.bind(this),
      selectedItems
    );
  }

  private async onCorrectRule(event: WebComponentEvent) {
    if (event.detail.rowData.item.configurationStatus === IntegrityMonitorConfigurationStatus.STATUS_SOURCE_DELETED) {
      await this.showDeleteDialog([event.detail.rowData.item]);
    } else {
      if (isEmpty(event.detail.rowData.item.stringVariables)) {
        await this.updateNonConfigurableDefaultRule(event.detail.rowData.item);
      } else {
        await this.showDefaultRuleCustomizationForm(event.detail.rowData.item);
      }
    }
  }

  private async updateNonConfigurableDefaultRule(rule: IntegrityMonitorRuleGridItem) {
    const errorMsg = this.dependencies.translateFn('Rule not updated.');
    let removeError = null;

    try {
      const response = await this.dataRepository.updateNonConfigurableDefaultRule(rule);

      if (!response || !response.success) {
        removeError = errorMsg;
      }
    } catch (error) {
      removeError = error.message || errorMsg;
    }

    if (removeError) {
      showErrorToast(removeError);
      return;
    }

    showSuccessToast(this.dependencies.translateFn('Rule updated successfully.'));

    this.view.dom.$integrityMonitorGrid.refreshGrid();
  }

  private async onConfirmDeleteRules(event: WebComponentEvent) {
    const unknownErrorMessage =
      event.detail.length > 1
        ? this.dependencies.translateFn('Rules not deleted.')
        : this.dependencies.translateFn('Rule not deleted.');

    const successMessage =
      event.detail.length > 1
        ? this.dependencies.translateFn('Rules deleted successfully.')
        : this.dependencies.translateFn('Rule deleted successfully.');

    const records = event.detail.map(record => {
      return {
        _id: record._id,
        companyId: record.companyId
      };
    });

    let removeError = null;

    try {
      const response = await this.dataRepository.removeRecords(records);

      if (!response || !response.success) {
        removeError = unknownErrorMessage;
      }
    } catch (error) {
      removeError = error.message || unknownErrorMessage;
    }

    if (removeError) {
      showErrorToast(removeError);
      return;
    }

    showSuccessToast(successMessage);

    await this.view.dom.$smartViews.controller.render();
    this.view.dom.$integrityMonitorGrid.refreshGrid();
  }

  private async onRuleSetCreated() {
    // Re-render smart views to fetch the newly created rule set
    await this.view.dom.$smartViews.controller.render();

    // Re-render grid to deselect all selected items
    await this.refreshGrid();
  }

  private async onRuleSetEdited(event: WebComponentEvent) {
    // Re-render smart views to update the edited rule set's name
    await this.view.dom.$smartViews.controller.render();

    // Update current grid title if required
    if (this.component.currentViewId === event.detail.ruleSetId) {
      this.view.dom.$integrityMonitorGrid.controller.view.updateTitle(event.detail.ruleSetName);
    }
  }

  private async onCustomRuleSave() {
    this.view.dom.$integrityMonitorGrid.canFetchRulesCountInRuleSet = true;
    await this.refreshGrid();
  }

  private async refreshGrid() {
    // Re-render grid to show the new rule
    this.view.dom.$integrityMonitorGrid.refreshGrid();
  }

  private async onRuleSetNameClick(event: CustomEvent) {
    // When clicking ruleset name close the form and filter the grid based on that ruleset
    this.view.dom.$integrityMonitorDefaultRuleCustomizationForm.controller.closeCustomizationForm();

    this.view.dom.$smartViews.dispatchEvent(
      new CustomEvent(SmartViewEvents.SMART_VIEW_SELECTED, {
        detail: {
          smartViewConfiguration: {
            id: event.detail.ruleSetId,
          },
        },
        bubbles: true,
      })
    );
  }

  private async companyChangedHandler(target: Target) {
    this.component.ruleIdsFilter = [];
    this.dependencies.setActiveCompany(target.companyId, target.companyName);

    await this.view.buildSmartViews(DefaultSmartViewId.ALL_RULES);
    await this.view.buildIntegrityMonitorGrid({
      activeViewId: DefaultSmartViewId.ALL_RULES,
      ruleIdsFilter: this.component.ruleIdsFilter,
    });
  }

  private async onPolicyNameClick() {
    this.view.setLoadingState();
  }
}
